// /opt/isp_erp/frontend/src/components/warehouse/DashboardSucursalPanel.jsx
import React, { useState, useEffect } from "react";
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Alert, CircularProgress, Chip,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import RouterOutlined from "@mui/icons-material/RouterOutlined";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import BuildOutlined from "@mui/icons-material/BuildOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import axios from "../../api/axios";
import { mensajeError } from "../../utils/mensajesError";
import { temaSucursales } from "../../pages/warehouse/sucursalesTema";
import ResumenDiario from "./ResumenDiario";

function Kpi({ Icono, titulo, valor, detalle, color, fondo }) {
  return (
    <Paper elevation={0} sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }}>
      <Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: fondo, color, flexShrink: 0 }}><Icono /></Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{titulo}</Typography>
        <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, lineHeight: 1.15 }}>{valor}</Typography>
        {detalle && <Typography variant="caption" color="text.secondary">{detalle}</Typography>}
      </Box>
    </Paper>
  );
}

export default function DashboardSucursalPanel() {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get("/warehouse/sucursales-equipos/dashboard")
      .then((r) => setD(r.data))
      .catch((e) => setError(mensajeError(e, "No se pudo cargar el dashboard de tu sucursal")));
  }, []);

  if (error) return (<Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>);
  if (!d) return (<Box sx={{ p: 6, textAlign: "center" }}><CircularProgress /></Box>);
  const e = d.equipos;
  const v = d.vinetas;

  return (
    <ThemeProvider theme={temaSucursales}>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Box sx={{ borderRadius: 4, p: { xs: 2.5, md: 3 }, mb: 3, color: "#fff", background: "linear-gradient(135deg, #1e3a8a 0%, #2563eb 55%, #38bdf8 100%)" }}>
          <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 2 }}>Dashboard</Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{d.sucursal_nombre}</Typography>
          <Typography sx={{ opacity: 0.92, mt: 1 }}>Lo que tiene tu sucursal: equipos, viñetas, técnicos y material.</Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}><Kpi Icono={RouterOutlined} titulo="Equipos en total" valor={e.total} detalle={`${e.en_inventario} en la sucursal`} color="#7c3aed" fondo="#f5f3ff" /></Grid>
          <Grid item xs={6} md={3}><Kpi Icono={Inventory2Outlined} titulo="Disponibles" valor={e.disponibles} detalle="Listos para asignar" color="#16a34a" fondo="#f0fdf4" /></Grid>
          <Grid item xs={6} md={3}><Kpi Icono={BuildOutlined} titulo="En revisión" valor={e.en_revision} detalle={`${e.en_rma} en RMA en la central`} color="#d97706" fondo="#fffbeb" /></Grid>
          <Grid item xs={6} md={3}><Kpi Icono={GroupsOutlined} titulo="Con técnicos" valor={e.con_tecnicos} detalle={`${e.retiro_pendiente} por devolver`} color="#2563eb" fondo="#eff6ff" /></Grid>
          <Grid item xs={6} md={3}><Kpi Icono={LocalShippingOutlined} titulo="En camino" valor={e.en_camino} detalle={`Envíos de la central · ${e.en_devolucion || 0} devolviendo`} color="#d97706" fondo="#fffbeb" /></Grid>
          <Grid item xs={6} md={3}><Kpi Icono={LocalOfferOutlined} titulo="Viñetas" valor={v.en_inventario + v.con_tecnicos} detalle={`${v.en_inventario} aquí · ${v.con_tecnicos} con técnicos · ${v.en_camino} en camino`} color="#0891b2" fondo="#ecfeff" /></Grid>
          <Grid item xs={6} md={3}><Kpi Icono={Inventory2Outlined} titulo="Materiales" valor={d.materiales.en_sucursal} detalle={`tipos con stock aquí · ${d.materiales.en_tecnicos} con técnicos`} color="#2563eb" fondo="#eff6ff" /></Grid>
          <Grid item xs={6} md={3}><Kpi Icono={GroupsOutlined} titulo="Personal" valor={d.personal.tecnicos} detalle={`técnicos · ${d.personal.encargados} encargado(s)`} color="#16a34a" fondo="#f0fdf4" /></Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }}>
              <Typography variant="h6" gutterBottom>Equipos de la sucursal por modelo</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Modelo</TableCell><TableCell align="right">Total</TableCell><TableCell align="right">En revisión</TableCell></TableRow></TableHead>
                  <TableBody>
                    {e.por_modelo.length === 0 && (<TableRow><TableCell colSpan={3} align="center">Tu sucursal no tiene equipos en el inventario</TableCell></TableRow>)}
                    {e.por_modelo.map((m) => (
                      <TableRow key={m.modelo}>
                        <TableCell>{m.modelo}</TableCell>
                        <TableCell align="right"><strong>{m.total}</strong></TableCell>
                        <TableCell align="right">{m.en_revision > 0 ? <Chip size="small" color="warning" label={m.en_revision} /> : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }}>
              <Typography variant="h6" gutterBottom>Lo que tiene cada técnico</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Técnico</TableCell><TableCell align="right">Equipos</TableCell><TableCell align="right">Por devolver</TableCell><TableCell align="right">Viñetas</TableCell></TableRow></TableHead>
                  <TableBody>
                    {d.tecnicos.length === 0 && (<TableRow><TableCell colSpan={4} align="center">No hay técnicos en tu sucursal (Sucursales → Personal)</TableCell></TableRow>)}
                    {d.tecnicos.map((t) => (
                      <TableRow key={t.tecnico_id}>
                        <TableCell>{t.tecnico}</TableCell>
                        <TableCell align="right"><strong>{t.equipos}</strong></TableCell>
                        <TableCell align="right">{t.retiro_pendiente > 0 ? <Chip size="small" color="error" label={t.retiro_pendiente} /> : "—"}</TableCell>
                        <TableCell align="right">{t.vinetas}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        <ResumenDiario />
      </Box>
    </ThemeProvider>
  );
}
